/**
 * BYOS Tile (dashboard variant)
 * ==============================
 *
 * Compact surface for the "Bring Your Own Station" Wunderground integration
 * living inside the main dashboard weather stack. Three states:
 *
 *   1. No user station configured  → CTA to /byos to connect one
 *   2. Station configured + live   → show temp / humidity / wind with
 *                                    a "hyper-local" badge
 *   3. Station errored             → show graceful fallback + retry link
 *
 * Intentionally smaller than the standalone /byos dashboard so it coexists
 * with WeatherIntelligence without dominating the scroll.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Broadcast, ArrowRight, WifiHigh } from '@phosphor-icons/react';

import { API_URL } from '../config/api';

export const BYOSDashboardTile = () => {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/pws/mine`, { withCredentials: true });
        if (!cancelled) {
          if (r.data?.status === 'ok') {
            setState({ loading: false, data: r.data, err: null });
          } else {
            // 'ok' not set — station configured but upstream returned empty
            setState({ loading: false, data: null, err: r.data?.message || 'no data' });
          }
        }
      } catch (e) {
        if (!cancelled) {
          const s = e?.response?.status;
          // 404 = no default station configured — that's the "connect CTA" state
          setState({
            loading: false,
            data: null,
            err: s === 404 ? 'not_configured' : (e?.response?.data?.detail || 'offline'),
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white/60 p-4 h-24 animate-pulse" data-testid="byos-tile-loading" />
    );
  }

  if (state.err === 'not_configured' || !state.data) {
    const subtitle = state.err === 'not_configured'
      ? 'Pair your Wunderground PWS for hyper-local data.'
      : `Station not reporting · ${state.err}. Check at /byos.`;
    return (
      <button
        onClick={() => navigate('/byos')}
        className="w-full text-left rounded-xl border border-dashed border-slate-300 bg-white hover:bg-slate-50 transition-colors px-4 py-3 flex items-center justify-between"
        data-testid="byos-tile-cta"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-slate-100 p-2">
            <Broadcast size={18} className="text-slate-700" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Your PWS</div>
            <div className="text-sm font-semibold text-slate-900">Bring Your Own Station</div>
            <div className="text-xs text-slate-500">{subtitle}</div>
          </div>
        </div>
        <ArrowRight size={16} className="text-slate-500" />
      </button>
    );
  }

  const obs = state.data.observations?.[0] || state.data;
  // Wunderground payload: both 'imperial' and root-level keys may exist
  const imp = obs.imperial || obs;
  const tempF = imp.temp ?? imp.tempAvg ?? null;
  const rh = obs.humidity ?? imp.humidityAvg ?? null;
  const windMph = imp.windSpeed ?? imp.windspeedAvg ?? null;
  const gustMph = imp.windGust ?? imp.windgustHigh ?? null;
  const stationName = state.data.station_id || 'Your PWS';

  return (
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4" data-testid="byos-tile-live">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <WifiHigh size={16} className="text-emerald-700" />
          <span className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">
            Hyper-local · {stationName}
          </span>
        </div>
        <button
          onClick={() => navigate('/byos')}
          className="text-[11px] uppercase tracking-widest text-slate-600 hover:text-slate-900"
          data-testid="byos-tile-expand-link"
        >
          full view →
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Temp</div>
          <div className="text-2xl font-semibold text-slate-900 tabular-nums">
            {tempF != null ? `${Math.round(tempF)}°F` : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">RH</div>
          <div className="text-2xl font-semibold text-slate-900 tabular-nums">
            {rh != null ? `${Math.round(rh)}%` : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Wind</div>
          <div className="text-2xl font-semibold text-slate-900 tabular-nums">
            {windMph != null ? `${Math.round(windMph)}` : '—'}
            {gustMph != null ? <span className="text-sm text-slate-500 ml-1">/{Math.round(gustMph)}g</span> : null}
          </div>
        </div>
      </div>
      {state.data.analytics?.risk_summary ? (
        <div className="text-xs text-slate-600 mt-3 italic" data-testid="byos-tile-risk">
          {state.data.analytics.risk_summary}
        </div>
      ) : null}
    </div>
  );
};
